/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useId } from 'react';

import {
  type FormFieldControlProps,
  type FormFieldMessageSeverity,
  type FormFieldProps,
  type FormFieldState,
} from './form-control-types';

type UseFormFieldOptions = FormFieldProps & {
  /**
   * Base for the generated ids, so a component that mints sibling ids of its
   * own (like the Combobox listbox and panel ids) can share one base.
   * Defaults to a generated id.
   */
  baseId?: string;
  /**
   * Overrides the generated control id, for controls that already name their
   * id (the Combobox trigger is `${baseId}-trigger`).
   */
  controlId?: string;
  /**
   * Ids referenced by `aria-describedby` before the message id, skipping
   * falsy entries (such as the TextInput clear/loading hint).
   */
  extraDescribedByIds?: (string | false | undefined)[];
};

type UseFormFieldResult = {
  field: FormFieldState;
  /** ARIA attributes to spread onto the control the field describes. */
  controlProps: FormFieldControlProps;
};

/**
 * Resolves the shared form field state: id generation, which of help and
 * error is displayed - an error always replaces the help text - and the ARIA
 * wiring of the control. The parts are rendered by `FormFieldLabel` and
 * `FormFieldMessage`.
 */
export const useFormField = ({
  baseId,
  controlId,
  label,
  helpText,
  errorText,
  isRequired = false,
  hasRequiredIndicator = false,
  hasVisuallyHiddenLabel = false,
  extraDescribedByIds = [],
}: UseFormFieldOptions): UseFormFieldResult => {
  const generatedBaseId = useId();
  const base = baseId ?? generatedBaseId;
  const labelId = `${base}-label`;
  const helpTextId = `${base}-help-text`;
  const errorTextId = `${base}-error-text`;
  const resolvedControlId = controlId ?? `${base}-control`;

  const hasError = Boolean(errorText);
  const hasHelpText = Boolean(helpText) && !hasError;
  const messageSeverity: FormFieldMessageSeverity | undefined = hasError
    ? 'error'
    : hasHelpText
      ? 'help'
      : undefined;
  const message = hasError ? errorText : hasHelpText ? helpText : undefined;
  const messageId = hasError
    ? errorTextId
    : hasHelpText
      ? helpTextId
      : undefined;

  const describedByIds = [...extraDescribedByIds, messageId].filter(
    (id): id is string => typeof id === 'string' && id !== '',
  );

  return {
    controlProps: {
      'aria-describedby':
        describedByIds.length > 0 ? describedByIds.join(' ') : undefined,
      'aria-invalid': hasError ? true : undefined,
    },
    field: {
      controlId: resolvedControlId,
      errorText,
      errorTextId,
      hasError,
      hasRequiredIndicator,
      hasVisuallyHiddenLabel,
      helpText,
      helpTextId,
      isRequired,
      label,
      labelId,
      message,
      messageId,
      messageSeverity,
    },
  };
};
